import { isEmpty } from 'lodash';

import type { EffectParams, ModelConfig, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';

import { Brand, FeatureFlag, Site } from '../enums';
import { License } from '../types/Account';

export interface Help {
  openChat: boolean;
}

const reducers = {
  setChat: (
    state: Help,
    payload: {
      openChat: boolean;
    }
  ) => ({ ...state, ...payload })
};

const effects = {
  setOpenChat({ openChat }: { openChat: boolean }, { rootState, dispatch }: EffectParams) {
    dispatch.help.setChat({ openChat });
  }
};

const selectors = (slice: Slicer<Help>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getShouldOpenMessenger() {
    return createSelector(
      slice,
      (rootState) => select.device.isMobile(rootState),
      (rootState) => select.site.isDesktopEmbedded(rootState),
      (rootState) => select.site.isMobileEmbedded(rootState),
      (rootState) => select.locale.get(rootState),
      (rootState) => select.site.getBrand(rootState),
      (rootState) => select.feature.check(rootState, FeatureFlag.PSMessenger),
      (rootState) => select.feature.check(rootState, FeatureFlag.PSMessengerConfig),
      (rootState) => select.feature.check(rootState, FeatureFlag.PSMessengerToggle),
      (
        state,
        isMobile,
        isDesktopEmbedded,
        isMobileEmbedded,
        locale,
        brand,
        messengerFlag,
        messengerConfig,
        messengerToggle
      ) => {
        if (messengerToggle) {
          return messengerConfig;
        } else {
          return (
            messengerFlag &&
            !isDesktopEmbedded &&
            !(isMobile && !isMobileEmbedded) &&
            locale === 'en' &&
            (brand === Brand.PokerStars || brand === Brand.StarsCasino)
          );
        }
      }
    );
  },
  getShouldOpenBoldChat() {
    return createSelector(
      slice,
      (rootState) => select.locale.get(rootState),
      (rootState) => select.site.getId(rootState),
      (rootState) => select.site.showVegasContent(rootState),
      (rootState) => select.device.isMobile(rootState),
      (rootState) => select.location.getLocaleURL(rootState),
      (state, locale, site, showVegasContent, isMobile, localeURL) => {
        const useDefaultLocale = !localeURL;

        if ([Site.EE, Site.DK].includes(site)) return locale === 'en';

        if (
          [
            Site.IT,
            Site.ES,
            Site.FR,
            Site.PT,
            Site.COM,
            Site.UK,
            Site.NET,
            Site.USPA,
            Site.USNJ
          ].includes(site)
        ) {
          return useDefaultLocale;
        }

        if (site === Site.SE) {
          if (isMobile) return true;

          return useDefaultLocale;
        }

        if (site === Site.EU) {
          if (showVegasContent) return useDefaultLocale; // site: pokerstarsvegas.de or pokerstars.de

          return locale === 'de' || locale === 'en'; // site: pokerstars.eu
        }

        return false;
      }
    );
  },
  getUserInfo() {
    return createSelector(
      slice,
      (rootState) => select.session.isClientAuthenticated(rootState),
      (rootState) => select.account.get(rootState),
      (rootState) => select.account.getCurrency(rootState),
      (rootState) => select.session.get(rootState),
      (state, isClientAuthenticated, account, currency, { userWebId }) => {
        if (!isClientAuthenticated || isEmpty(account)) {
          return null;
        }

        const {
          flags,
          flags2,
          flags3,
          privileges,
          privileges2,
          privileges3,
          licenseId,
          userId,
          country
        } = account;

        return {
          userName: userId,
          webId: userWebId,
          license: licenseId ? License[licenseId] : undefined,
          currency,
          country,
          flags,
          flags2,
          flags3,
          privileges,
          privileges2,
          privileges3
        };
      }
    );
  },
  getSiteInfo() {
    return createSelector(
      slice,
      (rootState) => select.site.showVegasContent(rootState),
      (rootState) => select.site.getBrandName(rootState),
      (rootState) => select.site.getCountry(rootState),
      (rootState) => select.site.getName(rootState),
      (rootState) => select.locale.get(rootState),
      (rootState) => select.location.getHostname(rootState),
      (state, showVegasContent, brandName, ipCountry, siteName, locale, domain) => {
        let siteLP = siteName;
        if (showVegasContent) {
          siteLP = 'DE';
        }

        return {
          brand: brandName,
          locale,
          site: siteLP,
          ipCountry,
          domain
        };
      }
    );
  },
  getShouldHideLogin() {
    return createSelector(
      slice,
      (rootState) => select.site.getId(rootState),
      (rootState) => select.site.isEmbedded(rootState),
      (rootState) => select.session.isAuthenticated(rootState),
      (rootState) => select.feature.check(rootState, FeatureFlag.PSMessengerIgnoreLogin),
      (state, site, isEmbedded, isAuthenticated, ignoreLogin) =>
        isEmbedded || isAuthenticated || site === Site.CH || ignoreLogin
    );
  }
});

export interface HelpModel extends ModelConfig<Help> {
  state: Help;
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const help: HelpModel = {
  state: { openChat: false },
  reducers,
  effects,
  selectors
};

export default help;
